<template>
  <div :id="id ? `${id}-form-input` : undefined" class="form-input" :data-test="dataTest">
    <FormLabel class="form-label" :title="title" :subtitle="subtitle" />
    <PreLoaderSection v-if="isLoading" :options="{ height: 34 }" />
    <Input
      v-else
      :id="id"
      :disabled="disabled"
      :inputType="inputType"
      :placeholder="placeholder"
      :min="min"
      :max="max"
      :maxlength="maxlength"
      :onBlur="(e) => {
        if (onBlur) {
          onBlur(e);
        }

        isShowHelpText = false;
      }"
      :onChange="onChange"
      :modelValue="modelValue"
      :value="value"
      :onKeyDown="onKeyDown"
      :onInput="onInput"
      :onPressedEnter="onPressedEnter"
      :isShowErrorMessage="isShowErrorMessage"
      :prefixText="prefixText"
      :suffixText="suffixText"
      :maxWidth="maxWidth"
      :isShowCharacterCount="isShowCharacterCount"
      @focus="(e: FocusEvent) => {
        if (onFocus) {
          onFocus(e);
        }

        isShowHelpText = true;
      }"
      @update:modelValue="(value) => emit('update:modelValue', value)"
    />
    <FormErrorMessage v-if="errorMessage && isShowErrorMessage" :errorMessage="errorMessage" />
    <FormHelpText
      v-if="helpText && !(errorMessage && isShowErrorMessage) && isShowHelpText"
      :helpText="helpText"
    />
  </div>
</template>

<script lang="ts" setup>
import Input from '@/modules/shared/components/atoms/input/Input.vue';
import PreLoaderSection from '@/modules/shared/components/atoms/preLoaderSection/PreLoaderSection.vue';
import FormLabel from '@/modules/shared/components/molecules/formLabel/FormLabel.vue';
import FormHelpText from '@/modules/shared/components/atoms/formHelpText/FormHelpText.vue';
import FormErrorMessage from '@/modules/shared/components/atoms/formErrorMessage/FormErrorMessage.vue';
import { InputProps } from '@/modules/shared/types/input.type';
import { ref, toRefs } from 'vue';

interface FormInputProps extends InputProps {
  isLoading?: boolean;
  errorMessage?: string | null;
  title?: string;
  subtitle?: string;
  helpText?: string;
  dataTest?: string;
}

const props = defineProps<FormInputProps>();
const {
  id,
  disabled,
  isLoading,
  inputType,
  placeholder,
  min,
  max,
  maxlength,
  isShowErrorMessage,
  errorMessage,
  modelValue,
  title,
  subtitle,
  helpText,
  onBlur,
  onFocus,
  onChange,
  onKeyDown,
  onInput,
  prefixText,
  suffixText,
  maxWidth,
  isShowCharacterCount,
  dataTest,
  value,
} = toRefs(props);

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const isShowHelpText = ref(false);
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/global-variables.scss';
.form-label {
  margin-bottom: $spacing-8;
}
</style>
